import axiosInstance from '../axios.config';
import {
  UserPreferences,
  SecuritySettings,
  ActiveSession,
  PasswordChangeRequest,
  TwoFactorSetupResponse,
  NotificationDTO,
  NotificationStats,
  SupportTicket,
  TicketMessage,
  CreateTicketRequest,
  FAQItem,
  FAQCategory,
  AnalyticsOverview,
  ChannelAnalytics,
  CampaignAnalytics,
  GeographicAnalytics,
  TimeAnalytics,
} from '../../types/settings';

// ============ User Preferences ============

export const preferencesService = {
  get: async (): Promise<UserPreferences> => {
    const response = await axiosInstance.get('/settings/preferences');
    return response.data;
  },

  update: async (preferences: Partial<UserPreferences>): Promise<UserPreferences> => {
    const response = await axiosInstance.put('/settings/preferences', preferences);
    return response.data;
  },

  resetToDefault: async (): Promise<UserPreferences> => {
    const response = await axiosInstance.post('/settings/preferences/reset');
    return response.data;
  },
};

// ============ Security Settings ============

export const securityService = {
  getSettings: async (): Promise<SecuritySettings> => {
    const response = await axiosInstance.get('/settings/security');
    return response.data;
  },

  changePassword: async (request: PasswordChangeRequest): Promise<{ success: boolean; message: string }> => {
    const response = await axiosInstance.post('/settings/security/password', request);
    return response.data;
  },

  setupTwoFactor: async (method: '2fa_app' | 'sms' | 'email'): Promise<TwoFactorSetupResponse> => {
    const response = await axiosInstance.post('/settings/security/2fa/setup', { method });
    return response.data;
  },

  verifyTwoFactor: async (code: string): Promise<{ success: boolean; backupCodes?: string[] }> => {
    const response = await axiosInstance.post('/settings/security/2fa/verify', { code });
    return response.data;
  },

  disableTwoFactor: async (code: string): Promise<{ success: boolean }> => {
    const response = await axiosInstance.post('/settings/security/2fa/disable', { code });
    return response.data;
  },

  getSessions: async (): Promise<{ sessions: ActiveSession[]; totalSessions: number; maxSessionsAllowed: number; availableSlots: number }> => {
    const response = await axiosInstance.get('/user-sessions');
    return response.data.data; // Unwrap ApiResponse
  },

  getSessionStats: async (): Promise<{ activeSessionCount: number; maxSessionsAllowed: number; availableSlots: number }> => {
    const response = await axiosInstance.get('/user-sessions/stats');
    return response.data.data; // Unwrap ApiResponse
  },

  revokeSession: async (sessionId: string): Promise<void> => {
    await axiosInstance.delete(`/user-sessions/${sessionId}`);
  },

  revokeAllSessions: async (): Promise<void> => {
    await axiosInstance.post('/user-sessions/logout-others');
  },

  logoutAllSessions: async (): Promise<void> => {
    await axiosInstance.post('/user-sessions/logout-all');
  },

  updateSessionTimeout: async (minutes: number): Promise<void> => {
    await axiosInstance.put('/settings/security/session-timeout', { minutes });
  },

  updateIpWhitelist: async (ips: string[]): Promise<void> => {
    await axiosInstance.put('/settings/security/ip-whitelist', { ips });
  },
};

// ============ Notifications ============

export const notificationService = {
  list: async (params?: {
    category?: string;
    read?: boolean;
    page?: number;
    size?: number;
  }): Promise<{ content: NotificationDTO[]; totalElements: number; totalPages: number }> => {
    const response = await axiosInstance.get('/notifications', {
      params: { ...params, page: params?.page !== undefined ? Math.max(0, params.page - 1) : 0 },
    });
    return response.data;
  },

  getStats: async (): Promise<NotificationStats> => {
    const response = await axiosInstance.get('/notifications/stats');
    return response.data;
  },

  markAsRead: async (id: string): Promise<void> => {
    await axiosInstance.post(`/notifications/${id}/read`);
  },

  markAllAsRead: async (): Promise<void> => {
    await axiosInstance.post('/notifications/read-all');
  },

  delete: async (id: string): Promise<void> => {
    await axiosInstance.delete(`/notifications/${id}`);
  },

  deleteAll: async (): Promise<void> => {
    await axiosInstance.delete('/notifications');
  },

  getUnreadCount: async (): Promise<number> => {
    const response = await axiosInstance.get('/notifications/unread-count');
    return response.data.count;
  },
};

// ============ Support ============

export const supportService = {
  // Tickets
  listTickets: async (params?: {
    status?: string;
    category?: string;
    page?: number;
    size?: number;
  }): Promise<{ content: SupportTicket[]; totalElements: number; totalPages: number }> => {
    const response = await axiosInstance.get('/support/tickets', {
      params: { ...params, page: params?.page !== undefined ? Math.max(0, params.page - 1) : 0 },
    });
    return response.data;
  },

  getTicket: async (id: string): Promise<SupportTicket> => {
    const response = await axiosInstance.get(`/support/tickets/${id}`);
    return response.data;
  },

  createTicket: async (request: CreateTicketRequest): Promise<SupportTicket> => {
    const formData = new FormData();
    formData.append('subject', request.subject);
    formData.append('description', request.description);
    formData.append('category', request.category);
    formData.append('priority', request.priority);
    if (request.attachments) {
      request.attachments.forEach((file) => {
        formData.append('attachments', file);
      });
    }
    const response = await axiosInstance.post('/support/tickets', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  replyToTicket: async (ticketId: string, message: string, attachments?: File[]): Promise<TicketMessage> => {
    const formData = new FormData();
    formData.append('message', message);
    if (attachments) {
      attachments.forEach((file) => {
        formData.append('attachments', file);
      });
    }
    const response = await axiosInstance.post(`/support/tickets/${ticketId}/messages`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  closeTicket: async (ticketId: string): Promise<void> => {
    await axiosInstance.patch(`/support/tickets/${ticketId}`, { status: 'CLOSED' });
  },

  reopenTicket: async (ticketId: string): Promise<void> => {
    await axiosInstance.patch(`/support/tickets/${ticketId}`, { status: 'OPEN' });
  },

  // FAQ
  getFAQCategories: async (): Promise<FAQCategory[]> => {
    const response = await axiosInstance.get('/support/faq/categories');
    return response.data;
  },

  getFAQsByCategory: async (category: string): Promise<FAQItem[]> => {
    const response = await axiosInstance.get(`/support/faq/category/${category}`);
    return response.data;
  },

  searchFAQ: async (query: string): Promise<FAQItem[]> => {
    const response = await axiosInstance.get('/support/faq/search', { params: { q: query } });
    return response.data;
  },

  rateFAQ: async (faqId: string, helpful: boolean): Promise<void> => {
    await axiosInstance.post(`/support/faq/${faqId}/rate`, { helpful });
  },
};

// ============ Analytics ============

export const analyticsService = {
  getOverview: async (params: {
    startDate: string;
    endDate: string;
    organizationId?: string;
  }): Promise<AnalyticsOverview> => {
    const response = await axiosInstance.get('/analytics/overview', { params });
    return response.data;
  },

  getChannelAnalytics: async (params: {
    channel: string;
    startDate: string;
    endDate: string;
    organizationId?: string;
  }): Promise<ChannelAnalytics> => {
    const response = await axiosInstance.get('/analytics/channel', { params });
    return response.data;
  },

  getCampaignAnalytics: async (params: {
    startDate: string;
    endDate: string;
    organizationId?: string;
  }): Promise<CampaignAnalytics> => {
    const response = await axiosInstance.get('/analytics/campaigns', { params });
    return response.data;
  },

  // Note: Geographic analytics endpoint not yet implemented in backend
  // getGeographicAnalytics: async (params: {
  //   startDate: string;
  //   endDate: string;
  //   organizationId?: string;
  // }): Promise<GeographicAnalytics[]> => {
  //   const response = await axiosInstance.get('/analytics/geographic', { params });
  //   return response.data;
  // },

  getTimeAnalytics: async (params: {
    startDate: string;
    endDate: string;
    organizationId?: string;
  }): Promise<TimeAnalytics> => {
    const response = await axiosInstance.get('/analytics/time', { params });
    return response.data;
  },

  exportReport: async (params: {
    type: 'overview' | 'channel' | 'campaign' | 'geographic';
    format: 'csv' | 'pdf' | 'excel';
    startDate: string;
    endDate: string;
    organizationId?: string;
  }): Promise<Blob> => {
    const response = await axiosInstance.get('/analytics/export', {
      params,
      responseType: 'blob',
    });
    return response.data;
  },
};

export default {
  preferences: preferencesService,
  security: securityService,
  notifications: notificationService,
  support: supportService,
  analytics: analyticsService,
};
